import * as React from 'react';
import axios, { CancelTokenSource, CancelTokenStatic } from 'axios';
import clsx from 'clsx';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { String_System_ConnectionError, String_System_GeneralError } from '../../utilities/StringHelper';
import { CheckStateContext } from '../../providers/CheckStateContextProvider';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe, StripeElementsOptions} from '@stripe/stripe-js';
import StripeCheckout from './StripeCheckout'
import { CreatePaymentRequest } from '../../models/payment/CreatePaymentRequest';
import { CreatePaymentResponse } from '../../models/payment/CreatePaymentResponse';
import { PaymentMethodsAccepted } from '../../models/payment/PaymentMethodsAccepted';


const useStyles = makeStyles({
    root: {
        textAlign: 'center',
        height: 'calc(100vh - ' + (ElementDimensions.appBarHeight + ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px)',
    },
    rootLandscape: {
        textAlign: 'center',
        height: '150%',
    },
});

export type StripeErrorHandler = () => any;
export type StripeBackClickHandler = () => any;

export interface StripePaymentProps {
    total: string,
    paymentMethodAllowed: PaymentMethodsAccepted | undefined,
    backHandler: StripeBackClickHandler,
    errorHandler: StripeErrorHandler,
}

export default function StripePayment(props: StripePaymentProps) {
    const { total, paymentMethodAllowed, backHandler, errorHandler } = props;
    const isPortrait = true;
    const [clientSecret, setClientSecret] = React.useState<string | null>(null);
    const [isAuthorized, setIsAuthorized] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(''); 
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const stripePromise = loadStripe(checkOutState.state.stripeApiKey!!, {stripeAccount: checkOutState.state.stripeAccountId!!});

    React.useEffect(() => {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        if (isAuthorized === false) {
            authorize(source);
        }
        
        return () => {
            CancelToken.source().cancel();
        };
    }, [clientSecret]);

    const authorize = (cancelTokenSource: CancelTokenSource) => {

        const request: CreatePaymentRequest = {
            tip: checkState.state.tip,
            tax: checkState.state.checkTotal!!.tax ,
            total: checkState.state.checkTotal!!.amount_due + checkState.state.tip,
            user_id: userState.state.userId!!,
            table_id: checkOutState.state.tableId,
            check_please_payment_id: checkState.state.paymentId!!
        };

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/create"

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as CreatePaymentResponse;

            if (r.success === true) {
                setIsAuthorized(true);
                setClientSecret(r.payment_token!!);                                
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
        });
    };

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    const paymentElementOptions: StripeElementsOptions = {
        clientSecret: clientSecret!!,
        appearance: {
            theme: 'flat',
        },
    };
    
    return (
        <div className={clsx({
            [classes.root]: isPortrait,
            [classes.rootLandscape]: !isPortrait,
        })}>
            {isAuthorized && clientSecret !== null &&
                <Elements stripe={stripePromise} options={paymentElementOptions}>
                    <StripeCheckout
                        total={total}
                        paymentMethodAllowed={paymentMethodAllowed}
                        backHandler={backHandler}
                        errorHandler={errorHandler} />
                </Elements>
            }

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>        
    );
}